import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/auth";
import { Header, Footer } from "../components/Layout";
import { DossierFlash } from "../components/DossierFlash";
import { BetaTestersRegistry } from "../components/BetaTestersRegistry";
import { Sparkles, Timer, Briefcase, Loader2, Upload, Zap, FileText, Clock, ChevronRight, Archive, X, Home, Receipt, Car, Download, Link2 } from "lucide-react";

const DEMO_CASES = {
  caution: {
    label: "Caution 850 €",
    icon: <Home className="w-4 h-4" />,
    title: "Caution non restituée",
    subtitle: "Bail · Restitution dépôt de garantie · Loi 89-462",
    data: {
      block1_faits: {
        chronologie: [
          { date: "30/06/2026", fait: "Fin du bail — état des lieux de sortie conforme, sans réserve" },
          { date: "31/07/2026", fait: "Expiration du délai légal de 1 mois pour restitution du dépôt" },
          { date: "02/09/2026", fait: "Aucun retour du bailleur ni justificatif de devis / facture" },
        ],
        montants: ["Dépôt de garantie : 850 €", "Majoration légale : 10 %/mois de retard commencé"],
        parties: ["Locataire (bénéficiaire)", "Bailleur (débiteur)"],
      },
      block2_ready_score: { pourcentage: 80, libelle: "80 % — Dossier solide, prêt pour mise en demeure" },
      block3_checklist: {
        pieces_presentes: ["Bail signé", "État des lieux d'entrée", "État des lieux de sortie conforme", "Attestation de restitution des clés"],
        pieces_manquantes: ["Copie du RIB locataire", "Accusé de réception de mise en demeure"],
      },
      block4_tracabilite: [
        { source: "Légifrance", texte: "Loi du 6 juillet 1989 — Art. 22 · Délais et pénalités de restitution", url: "https://www.legifrance.gouv.fr/loda/id/JORFTEXT000000509310/" },
        { source: "Service-Public.fr", texte: "Dépôt de garantie : délais, retenues et recours", url: "https://www.service-public.fr/particuliers/vosdroits/F31269" },
      ],
      meta: { seconds_generated: 47, time_saved_minutes: 270 },
    },
  },
  charges: {
    label: "Charges 420 €",
    icon: <Receipt className="w-4 h-4" />,
    title: "Régularisation de charges locatives",
    subtitle: "Rétroactivité sans justificatifs · Loi 89-462 · Art. 23",
    data: {
      block1_faits: {
        chronologie: [
          { date: "15/08/2026", fait: "Réception d'un décompte de régularisation portant sur 2 années" },
          { date: "15/08/2026", fait: "Absence du décompte individuel par lot et de la grille de tantièmes" },
          { date: "01/09/2026", fait: "Demande écrite du locataire de communication des justificatifs" },
        ],
        montants: ["Régularisation réclamée : 420 €", "Période concernée : 2 années"],
        parties: ["Locataire (contestataire)", "Bailleur (émetteur du décompte)"],
      },
      block2_ready_score: { pourcentage: 65, libelle: "65 % — Dossier incomplet, pièces à réclamer" },
      block3_checklist: {
        pieces_presentes: ["Bail signé", "Décompte de régularisation reçu", "Avis d'échéance des provisions"],
        pieces_manquantes: ["Arrêté des comptes approuvé en Assemblée Générale de copropriété", "Relevé individuel des provisions versées"],
      },
      block4_tracabilite: [
        { source: "Légifrance", texte: "Loi du 6 juillet 1989 — Art. 23 · Justificatifs de charges (6 mois)", url: "https://www.legifrance.gouv.fr/loda/article_lc/LEGIARTI000006475199/" },
        { source: "Service-Public.fr", texte: "Charges locatives : ce qui peut être réclamé", url: "https://www.service-public.fr/particuliers/vosdroits/F947" },
      ],
      meta: { seconds_generated: 47, time_saved_minutes: 240 },
    },
  },
  fps: {
    label: "FPS 35 €",
    icon: <Car className="w-4 h-4" />,
    title: "Contestation de FPS indu",
    subtitle: "Stationnement dématérialisé · CGCT · Art. L. 2333-87",
    data: {
      block1_faits: {
        chronologie: [
          { date: "12/09/2026 14:32", fait: "Ticket de stationnement dématérialisé réglé et valide" },
          { date: "12/09/2026 14:38", fait: "Contrôle sur voirie — relevé de plaque avec coquille (1 caractère)" },
          { date: "20/09/2026", fait: "Réception de l'avis de paiement FPS 35 €" },
        ],
        montants: ["Forfait Post-Stationnement : 35 €", "Ticket payé (horodatage exact du contrôle)"],
        parties: ["Usager (contestataire)", "Autorité de gestion du stationnement"],
      },
      block2_ready_score: { pourcentage: 90, libelle: "90 % — Prêt pour Recours Administratif Préalable Obligatoire" },
      block3_checklist: {
        pieces_presentes: ["Avis de paiement FPS reçu", "Capture d'écran ticket dématérialisé", "Horodatage validé de l'application"],
        pieces_manquantes: ["Justificatif bancaire du paiement", "Reçu électronique de l'application de stationnement"],
      },
      block4_tracabilite: [
        { source: "Légifrance", texte: "CGCT Art. L. 2333-87 · Forfait Post-Stationnement", url: "https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000034097876/" },
        { source: "Service-Public.fr", texte: "Contester un FPS · RAPO (30 jours)", url: "https://www.service-public.fr/particuliers/vosdroits/F31053" },
      ],
      meta: { seconds_generated: 47, time_saved_minutes: 210 },
    },
  },
};

export default function PreInstruction() {
  const { user, loading } = useAuth();
  const [selectedCase, setSelectedCase] = useState("caution");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (loading || !user) return;
    api.get("/preinstruction/history").then((r) => setHistory(r.data)).catch(() => setHistory([]));
  }, [user, loading, result]);

  const analyze = async () => {
    setError("");
    if (!user) { setError("Connexion requise pour la pré-instruction B2B."); setTimeout(() => (window.location.href = "/login"), 1200); return; }
    if (!file && description.length < 20) { setError("Fournissez une description ≥ 20 caractères OU un fichier PDF/DOCX."); return; }
    setBusy(true); setResult(null); setSelectedDetail(null);
    const fd = new FormData();
    if (description) fd.append("description", description);
    fd.append("case_type", "bail_litige");
    if (file) fd.append("file", file);
    try {
      const r = await api.post("/preinstruction/analyze", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      setResult(r.data);
    } catch (e) {
      const s = e.response?.status;
      const d = e.response?.data?.detail;
      if (s === 401 || s === 403) { setError("Connexion requise."); setTimeout(() => (window.location.href = "/login"), 1200); }
      else if (s === 429) setError(d || "Limite atteinte. Réessayez dans 10 min.");
      else if (s === 415) setError(d || "Format non supporté (PDF ou DOCX).");
      else if (s === 413) setError(d || "Fichier trop volumineux (max 15 Mo).");
      else setError(d || "Analyse échouée.");
    } finally { setBusy(false); }
  };

  const openHistoryItem = async (id) => {
    setSelectedHistoryId(id); setResult(null); setError("");
    try {
      const r = await api.get(`/preinstruction/${id}`);
      setSelectedDetail(r.data.data);
    } catch { setError("Impossible d'ouvrir cette pré-instruction."); }
  };

  const copyDemoLink = async () => {
    const url = typeof window !== "undefined" ? window.location.origin + "/preinstruction" : "";
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié dans le presse-papier");
    } catch {
      toast.error("Copie impossible — sélectionnez et copiez manuellement.");
    }
  };

  const activeCase = DEMO_CASES[selectedCase];
  const active = selectedDetail || result || activeCase.data;
  const isDemo = !selectedDetail && !result;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-10">
        <div className="j-label mb-2 sm:mb-3 flex items-center gap-2"><Briefcase className="w-3 h-3" /> Espace Pro & Jury · SaaS Pré-instruction · 49 €/mois</div>
        <h1 className="j-h1 font-heading" data-testid="preinstruction-title">Dossier Flash.<br/><span className="text-[#10B981]">Pré-instruit en 47 secondes.</span></h1>
        <p className="mt-3 sm:mt-4 text-base sm:text-lg text-slate-600 max-w-3xl">
          Infrastructure documentaire B2B. Extraction factuelle, détection des pièces manquantes et qualification neutre selon les textes applicables.
        </p>
        <p className="mt-2 text-sm text-slate-500 max-w-3xl hidden sm:block">
          Sources officielles vérifiables : <b>Légifrance, Judilibre et Service-Public.fr</b> (Licence Etalab 2.0).
        </p>

        {/* BANDEAU ROI MÉTIER */}
        <div className="mt-4 sm:mt-6 p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-slate-900 to-emerald-800 text-white shadow-lg" data-testid="roi-banner">
          <div className="flex items-center gap-3">
            <Timer className="w-6 h-6 text-emerald-300 flex-shrink-0" />
            <div className="text-sm sm:text-base">
              <b className="text-emerald-300">Gain opérationnel :</b> 4h30 de tri administratif réduites à <b>47 secondes</b> d'instruction automatisée. Rentabilité immédiate pour syndics et gestionnaires locatifs.
            </div>
          </div>
        </div>

        {/* SÉLECTEUR 3 CAS + QR */}
        <div className="mt-4 sm:mt-6 grid lg:grid-cols-4 gap-4 items-start" data-testid="demo-jury-panel">
          <div className="lg:col-span-3">
            <div className="j-label mb-2 sm:mb-3 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-emerald-600" /> Démo Jury interactive · 3 cas types</div>
            <div className="flex gap-2 overflow-x-auto pb-2" role="tablist" aria-label="Cas démo">
              {Object.entries(DEMO_CASES).map(([key, c]) => {
                const active = selectedCase === key;
                return (
                  <button
                    key={key}
                    onClick={() => { setSelectedCase(key); setResult(null); setSelectedDetail(null); setSelectedHistoryId(null); }}
                    role="tab"
                    aria-selected={active}
                    className={`min-h-[44px] px-4 py-2.5 rounded-xl font-heading font-black text-sm flex items-center gap-2 flex-shrink-0 transition-all ${active ? "bg-emerald-600 text-white shadow-lg scale-105" : "bg-white border border-slate-200 text-slate-700 hover:border-emerald-400"}`}
                    data-testid={`demo-tab-${key}`}
                  >
                    {c.icon} {c.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 text-xs text-slate-500">
              <b>{activeCase.title}</b> — {activeCase.subtitle}
            </div>
            {/* MOBILE — bouton copie de lien à la place du QR */}
            <button
              onClick={copyDemoLink}
              className="mt-3 md:hidden inline-flex items-center gap-2 min-h-[44px] px-4 py-2 rounded-xl bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:border-emerald-400"
              data-testid="copy-demo-link-btn"
            >
              <Link2 className="w-4 h-4 text-emerald-600" /> Copier le lien de la démo
            </button>
          </div>
          {/* QR — DESKTOP UNIQUEMENT */}
          <div className="hidden md:flex items-center gap-3 justify-start lg:justify-end" data-testid="demo-jury-qr">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=110x110&margin=4&bgcolor=ffffff&color=0F172A&data=${encodeURIComponent(typeof window !== "undefined" ? window.location.origin + "/preinstruction" : "")}`}
              alt="QR code Démo Jury JurisFlash"
              width={110} height={110} loading="lazy"
              className="rounded-lg bg-white p-1 shadow border border-slate-200 flex-shrink-0"
              data-testid="demo-jury-qr-img"
            />
            <div className="text-xs text-slate-600 max-w-[140px]">
              <div className="font-black uppercase tracking-widest text-emerald-700 text-[0.6rem]">Scannez pour partager</div>
              <div className="mt-1 leading-snug">Ouvre la démo publique sur mobile en 1 seconde.</div>
            </div>
          </div>
        </div>

        {/* DOSSIER FLASH */}
        {active && (
          <div className="mt-6" data-testid="dossier-flash-wrapper">
            <DossierFlash data={active} animated={true} />
            <div className="mt-6 rounded-xl bg-emerald-600 text-white px-5 py-4 flex items-center gap-3 shadow-md" data-testid="saas-metrics-banner">
              <Timer className="w-6 h-6 flex-shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-widest font-bold opacity-90">Métriques SaaS</div>
                <div className="font-heading font-black text-lg">
                  {((active.meta?.time_saved_minutes || 270) / 60).toFixed(1)}h de tri manuel économisées sur ce dossier
                </div>
              </div>
              <div className="ml-auto text-xs opacity-80">Généré en {active.meta?.seconds_generated || 47}s</div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <button onClick={() => window.print()} className="btn-emerald min-h-[48px]" data-testid="export-report-btn">
                <Download className="w-5 h-5" /> Exporter le Rapport d'Instruction Certifié (PDF)
              </button>
              {(selectedDetail || result) && (
                <button onClick={() => { setSelectedDetail(null); setSelectedHistoryId(null); setResult(null); }} className="btn-ghost min-h-[48px]" data-testid="back-to-demo-btn">
                  <X className="w-4 h-4" /> Retour aux cas démo
                </button>
              )}
            </div>
            {isDemo && <div className="mt-3 text-xs text-slate-500 italic">Données de démonstration Jury · statiques et anonymisées.</div>}
          </div>
        )}

        {/* INJECTION */}
        <div className="mt-12 border-t-2 border-slate-200 pt-10" data-testid="b2b-injection-zone">
          <div className="j-label mb-2"><Upload className="w-3 h-3 inline mr-1" /> Injection dossier entrant</div>
          <h2 className="font-heading font-black text-2xl text-[#1E293B]">Pré-instruire un dossier gestionnaire</h2>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">Upload PDF/DOCX et/ou description texte. RGPD : aucun transfert automatique, données isolées de la vue citoyen.</p>

          <div className="mt-4 p-4 rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50/30" data-testid="b2b-upload-zone">
            <input ref={fileRef} type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="b2b-file-input" />
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <button onClick={() => fileRef.current?.click()} className="btn-ghost text-sm min-h-[44px] justify-center sm:justify-start" data-testid="b2b-file-btn">
                <Upload className="w-4 h-4" /> Choisir un PDF ou DOCX
              </button>
              {file && (
                <div className="flex items-center gap-2 text-sm text-slate-700 flex-wrap">
                  <FileText className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span className="font-mono text-xs break-all" data-testid="b2b-file-name">{file.name}</span>
                  <span className="text-xs text-slate-500 flex-shrink-0">({Math.round(file.size/1024)} Ko)</span>
                  <button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-xs text-red-600 hover:underline flex-shrink-0" data-testid="b2b-file-remove">retirer</button>
                </div>
              )}
              <span className="text-xs text-slate-500 sm:ml-auto">Max 15 Mo</span>
            </div>
          </div>

          <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Récit complémentaire optionnel — précisions non lisibles dans le PDF, contexte, dates spécifiques…" className="mt-4 w-full min-h-[140px] p-4 rounded-xl border-2 border-slate-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none text-sm" data-testid="b2b-description" />

          <div className="mt-3 flex items-center gap-3">
            <button onClick={analyze} disabled={busy} className="btn-emerald min-h-[48px]" data-testid="b2b-analyze-btn">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyse…</> : <><Zap className="w-4 h-4" /> Pré-instruire</>}
            </button>
            <span className="text-xs text-slate-500">{description.length} caractères{file ? ` + ${file.name}` : ""}</span>
          </div>
          {error && <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800" data-testid="b2b-error">{error}</div>}
        </div>

        {/* HISTORIQUE */}
        {user && (
          <div className="mt-12 border-t-2 border-slate-200 pt-10" data-testid="b2b-history-zone">
            <div className="j-label mb-2 flex items-center gap-1.5"><Archive className="w-3 h-3" /> Historique pré-instructions</div>
            <h2 className="font-heading font-black text-2xl text-[#1E293B]">Vos dossiers pré-instruits ({history.length})</h2>
            {history.length === 0 && <div className="text-slate-500 text-sm mt-3" data-testid="b2b-history-empty">Aucun dossier pré-instruit pour l'instant. Utilisez la zone d'injection ci-dessus.</div>}
            <div className="mt-4 space-y-2">
              {history.map((h) => (
                <button key={h.preinstruction_id} onClick={() => openHistoryItem(h.preinstruction_id)} className={`w-full text-left j-card !p-4 hover:border-emerald-400 transition-colors ${selectedHistoryId === h.preinstruction_id ? "border-emerald-500 bg-emerald-50/40" : ""}`} data-testid={`b2b-history-item-${h.preinstruction_id}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0"><FileText className="w-4 h-4 text-emerald-700" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono text-slate-500">{h.preinstruction_id}</span>
                        {h.ready_score != null && <span className={`text-[0.6rem] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${h.ready_score >= 75 ? "bg-emerald-100 text-emerald-800" : h.ready_score >= 50 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>Ready {h.ready_score}%</span>}
                      </div>
                      <p className="text-sm text-slate-700 mt-1 truncate">{h.corpus_preview || "(pas d'aperçu)"}</p>
                      <div className="text-xs text-slate-500 mt-1"><Clock className="w-3 h-3 inline mr-1" /> {h.created_at ? new Date(h.created_at).toLocaleString("fr-FR") : ""}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

      </div>
      <BetaTestersRegistry />
      <Footer />
    </div>
  );
}
